import React from "react"
import { useTranslation } from "react-i18next"
import { useGlobalBanner } from "../../hooks/useGlobalBanner"
import { useCRXModal } from "../providers/CRXModalProvider.jsx"
import styles from "./GlobalBanner.module.css"

const toneOptions = new Set(["info", "warning", "error", "success"])

const renderStatusIcon = (tone) => {
	switch (tone) {
		case "success":
			return (
				<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
					<circle
						cx="10"
						cy="10"
						r="8"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<path
						d="M6.5 10.5l2.5 2.5 4.5-5"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="1.5"
					/>
				</svg>
			)
		case "warning":
			return (
				<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
					<path
						d="M10 3l8 14H2L10 3z"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinejoin="round"
					/>
					<path
						d="M10 7.25v5"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeWidth="1.5"
					/>
					<circle cx="10" cy="14.25" r="1" fill="currentColor" />
				</svg>
			)
		case "error":
			return (
				<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
					<circle
						cx="10"
						cy="10"
						r="8"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<path
						d="M7 7l6 6M13 7l-6 6"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeWidth="1.5"
					/>
				</svg>
			)
		case "info":
		default:
			return (
				<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
					<circle
						cx="10"
						cy="10"
						r="8"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<circle cx="10" cy="6.5" r="1" fill="currentColor" />
					<path
						d="M10 9.5v5"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeWidth="1.5"
					/>
				</svg>
			)
	}
}

export const GlobalBanner = () => {
	const { t } = useTranslation()
	const { banner, isLoading, dismiss } = useGlobalBanner()
	const { open: openCRXModal } = useCRXModal()

	if (isLoading || !banner) {
		return null
	}

	const tone = toneOptions.has(banner.type) ? banner.type : "info"
	const isAlert = tone === "warning" || tone === "error"

	const renderLink = () => {
		if (!banner.linkText) return null

		if (banner.linkAction === "crx_modal") {
			return (
				<button
					type="button"
					className={styles.link}
					onClick={openCRXModal}
				>
					{banner.linkText}
				</button>
			)
		}

		if (banner.linkAction === "url" && banner.linkUrl) {
			return (
				<a
					className={styles.link}
					href={banner.linkUrl}
					target="_blank"
					rel="noreferrer noopener"
				>
					{banner.linkText}
				</a>
			)
		}

		return null
	}

	return (
		<div className={styles.container}>
			<div
				className={`${styles.banner} ${styles[tone]}`}
				role={isAlert ? "alert" : "status"}
				aria-live={isAlert ? "assertive" : "polite"}
			>
				<span className={styles.icon}>{renderStatusIcon(tone)}</span>
				<div className={styles.message}>
					<span>{banner.message}</span>
					{renderLink()}
				</div>
				{banner.dismissible && (
					<button
						type="button"
						className={styles.dismiss}
						onClick={dismiss}
						aria-label={t("common.dismiss_banner")}
					>
						<svg
							viewBox="0 0 20 20"
							aria-hidden="true"
							focusable="false"
						>
							<path
								d="M6 6l8 8M14 6l-8 8"
								fill="none"
								stroke="currentColor"
								strokeLinecap="round"
								strokeWidth="1.5"
							/>
						</svg>
					</button>
				)}
			</div>
		</div>
	)
}
